import { PurchaseRequestItemState, PurchaseRequestStatus } from "../../types";

export type StatusFilter = PurchaseRequestStatus | "ALL";

export enum BuyerViewMode {
  REQUESTS = "REQUESTS",
  ITEMS = "ITEMS",
  MY_REQUESTS = "MY_REQUESTS",
}

/** The `t` returned by `useTranslation()`/`i18next` — typed loosely since every call site across
 * this flow's split-out files only ever calls it with a plain key. */
export type TFunc = (key: string, options?: Record<string, unknown>) => string;

export interface StateFilterOption {
  key: string;
  label: string;
}

/** Buyer-side *operators* — Admin, Buyer, Manager, Analyst — get the tabbed list view whose
 * "My Requests" tab calls `/me/{page}` (PRs assigned to them). A Requester keeps the simplified
 * created-by-me view and never sees that tab.
 *
 * ADMIN must be in this set: admins had a dedicated buyer route tree (`BuyerAdminRoutes`) before
 * the RBAC/unified-routes merge, and dropping admin from this gate silently hid every admin's
 * assigned-to-me PRs (they never rendered the tab, so `/me/{page}` was never called). Regression
 * guard — do not remove admin without a replacement path. */
export const isBuyerOperatorView = (flags: {
  isAdmin: boolean;
  isBuyer: boolean;
  isManager: boolean;
  isAnalyst: boolean;
}): boolean =>
  flags.isAdmin || flags.isBuyer || flags.isManager || flags.isAnalyst;

/** Same `requestStateFilters` the pre-Carbon screen built inline: "All" plus every
 * `PurchaseRequestStatus`, labelled via the shared `purchaseRequests.status.*` keys. */
export const buildRequestStateFilters = (t: TFunc): StateFilterOption[] => [
  { key: "ALL", label: t("purchaseRequests.all") },
  ...Object.values(PurchaseRequestStatus).map((status) => ({
    key: status,
    label: t(`purchaseRequests.status.${status.toLowerCase()}`, {
      defaultValue: status,
    }),
  })),
];

/** Same `itemStateFilters` the pre-Carbon screen built inline for the items view. */
export const buildItemStateFilters = (t: TFunc): StateFilterOption[] => [
  { key: "ALL", label: t("purchaseRequests.all") },
  ...Object.values(PurchaseRequestItemState).map((state) => ({
    key: state,
    label: t(`purchaseRequests.status.${state.toLowerCase()}`, {
      defaultValue: state,
    }),
  })),
];

/** Buyer/Manager/Analyst views never offer DRAFT as a filter option — same exclusion the
 * pre-Carbon screen's `visibleStateFilters` applied. */
export const visibleStateFilters = (
  filters: StateFilterOption[],
  isBuyerView: boolean
): StateFilterOption[] =>
  isBuyerView
    ? filters.filter((filter) => filter.key !== PurchaseRequestStatus.DRAFT)
    : filters;
